// TaskID.js - Main component that orchestrates role-specific components
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getDoc } from "firebase/firestore";
import { auth, TASK_STATUS } from "../firebase.config.js";
import { useAuthState } from "react-firebase-hooks/auth";
import { useAgency } from '../AgencyContext';
import L1Component from "../TaskL1/L1Component";
import L2Component from "../TaskL2/L2Component";
import QAComponent from "../TaskQA/QAComponent";
import ReadOnlyView from "../TaskReadOnly/ReadOnlyView";
import CompletedTaskView from "../TaskCompleted/CompletedTaskView";

const TaskID = () => {
  const { id } = useParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user] = useAuthState(auth);
  const { currentAgency, getAgencyDoc } = useAgency();
  
  const userRole = currentAgency?.userRole;

  useEffect(() => {
    if (id && currentAgency) {
      fetchTask();
    }
  }, [id, currentAgency]);

  const fetchTask = async () => {
    try {
      const taskDocRef = getAgencyDoc('taskbacklog', id);
      if (!taskDocRef) return;
      
      const taskDoc = await getDoc(taskDocRef);
      if (taskDoc.exists()) {
        const taskData = { id: taskDoc.id, ...taskDoc.data() };
        console.log("Task loaded:", taskData);
        setTask(taskData);
        
      } else {
        alert("Task not found");
      }
    } catch (error) {
      console.error("Error loading task:", error);
      alert("Error loading task");
    } finally {
      setLoading(false);
    }
  };

  const canUserAccessTask = () => {
    if (!task || !userRole) {
      console.log("Cannot access - missing task or role:", { task: !!task, userRole });
      return false;
    }

    console.log("Checking access for:", {
      userRole,
      userRoleType: typeof userRole,
      userRoleExact: JSON.stringify(userRole),
      isManager: userRole === "manager",
      taskStatus: task.status,
      assignedTo: task.assignedTo,
      userEmail: user?.email,
      isAssignedToMe: task.assignedTo === user?.email,
      isUnassigned: !task.assignedTo
    });

    // Managers can access all tasks regardless of assignment
    if (userRole === "Manager") {
      console.log("Manager access granted");
      return true;
    }

    // User can access if task is not assigned or assigned to them
    if (!task.assignedTo || task.assignedTo === user.email) {
      switch (task.status) {
        case TASK_STATUS.NOT_STARTED_L1:
        case TASK_STATUS.TO_BE_REWORKED_L1:
          const canAccessL1 = userRole === "L1";
          console.log("L1 access check:", canAccessL1);
          return canAccessL1;
        case TASK_STATUS.NOT_STARTED_L2:
        case TASK_STATUS.TO_BE_REWORKED_L2:
          const canAccessL2 = userRole === "L2";
          console.log("L2 access check:", canAccessL2);
          return canAccessL2;
        case TASK_STATUS.PENDING_QA_L1:
        case TASK_STATUS.PENDING_QA_L2:
          const canAccessQA = userRole === "QA";
          console.log("QA access check:", canAccessQA);
          return canAccessQA;
        default:
          console.log("Unknown task status:", task.status);
          return false;
      }
    }
    console.log("Access denied - task assigned to someone else");
    return false;
  };

  // Show loading while fetching task or agency
  if (loading || !currentAgency) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#212121',
        zIndex: 9999
      }}>
      </div>
    );
  }

  if (!task) {
    return <div className="error">Task not found</div>;
  }

  if (!user) {
    return (
      <div className="error">You must be logged in to access this task</div>
    );
  }

  if (!userRole) {
    return (
      <div className="error">You don't have permission to access this application</div>
    );
  }

  const isAssignedToMe = task.assignedTo === user.email;
  const isAssignedToOther = task.assignedTo && !isAssignedToMe;
  const canAccess = canUserAccessTask();
  const isCompleted =
    task.status === TASK_STATUS.VALIDATED_L1 ||
    task.status === TASK_STATUS.VALIDATED_L2;

  console.log("Render decision:", {
    isCompleted,
    isAssignedToOther,
    isAssignedToMe,
    canAccess,
    userRole,
    taskStatus: task.status
  });

  // Determine which component to render based on role and task status
  const renderTaskComponent = () => {
    // For completed tasks, show the simplified download view
    if (isCompleted) {
      return <CompletedTaskView task={task} taskId={id} user={user} />;
    }

    if ((isAssignedToOther || (!isAssignedToMe && task.assignedTo)) && userRole !== "Manager") {
      return <ReadOnlyView task={task} userRole={userRole} />;
    }

    if (!canAccess) {
      return <ReadOnlyView task={task} userRole={userRole} />;
    }

    // Role-specific components
    if (
      (userRole === "L1" || userRole === "Manager") &&
      (task.status === TASK_STATUS.NOT_STARTED_L1 ||
        task.status === TASK_STATUS.TO_BE_REWORKED_L1)
    ) {
      console.log("Rendering L1Component");
      return (
        <L1Component
          task={task}
          taskId={id}
          user={user}
          onTaskUpdate={fetchTask}
        />
      );
    }

    if (
      (userRole === "L2" || userRole === "Manager") &&
      (task.status === TASK_STATUS.NOT_STARTED_L2 ||
        task.status === TASK_STATUS.TO_BE_REWORKED_L2)
    ) {
      console.log("Rendering L2Component");
      return (
        <L2Component
          task={task}
          taskId={id}
          user={user}
          userRole={userRole}
          onTaskUpdate={fetchTask}
        />
      );
    }

    if (
      (userRole === "QA" || userRole === "Manager") &&
      (task.status === TASK_STATUS.PENDING_QA_L1 ||
        task.status === TASK_STATUS.PENDING_QA_L2)
    ) {
      console.log("Rendering QAComponent");
      return (
        <QAComponent
          task={task}
          taskId={id}
          user={user}
          userRole={userRole}
          onTaskUpdate={fetchTask}
        />
      );
    }

    console.log("Fallback to ReadOnlyView");
    return <ReadOnlyView task={task} userRole={userRole} />;
  };

  return <div className="task-container">{renderTaskComponent()}</div>;
};

export default TaskID;